
import { useState, useEffect } from 'react';

interface Review {
  id: number;
  rating: number;
  comment: string;
  date: string;
  treatmentType?: string;
}

interface ReviewMetrics {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    [key: number]: number;
  };
}

// Sample review data
const sampleReviews: Review[] = [
  {
    id: 1,
    rating: 5,
    comment: "Dr. Smith provided excellent care. The dental implant procedure was painless and the results are fantastic. Highly recommended!",
    date: "2025-03-15",
    treatmentType: "Dental Implant"
  },
  {
    id: 2,
    rating: 5,
    comment: "Very thorough and professional. Took the time to explain everything in detail and answered all my questions.",
    date: "2025-03-10",
    treatmentType: "Consultation"
  },
  {
    id: 3,
    rating: 4,
    comment: "Good experience overall. Wait time was a bit long but the care provided was excellent.",
    date: "2025-03-05",
    treatmentType: "Teeth Cleaning"
  },
  {
    id: 4,
    rating: 5,
    comment: "The teeth whitening results exceeded my expectations. Very satisfied with the outcome.",
    date: "2025-02-25",
    treatmentType: "Teeth Whitening"
  },
  {
    id: 5,
    rating: 3,
    comment: "Procedure was fine, but communication regarding aftercare could have been better.",
    date: "2025-02-20",
    treatmentType: "Root Canal"
  },
  {
    id: 6,
    rating: 5,
    comment: "Very gentle and attentive. Made me feel comfortable throughout the entire procedure.",
    date: "2025-02-15",
    treatmentType: "Extraction"
  },
  {
    id: 7,
    rating: 4,
    comment: "Clear explanation of my treatment options. Appreciated the transparency regarding costs.",
    date: "2025-02-10"
  },
  {
    id: 8,
    rating: 2,
    comment: "Appointment scheduling was confusing and I had to wait for a long time.",
    date: "2025-02-05"
  }
];

export const useReviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [metrics, setMetrics] = useState<ReviewMetrics>({
    averageRating: 0,
    totalReviews: 0,
    ratingDistribution: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Simulate API call to fetch reviews
    setLoading(true);
    
    setTimeout(() => {
      try {
        // Sort reviews by date (newest first)
        const sortedReviews = [...sampleReviews].sort((a, b) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        
        setReviews(sortedReviews);
        
        // Calculate metrics
        const totalReviews = sortedReviews.length;
        const sumRatings = sortedReviews.reduce((sum, review) => sum + review.rating, 0);
        const averageRating = sumRatings / totalReviews;
        
        // Calculate rating distribution
        const ratingCounts: {[key: number]: number} = {1: 0, 2: 0, 3: 0, 4: 0, 5: 0};
        sortedReviews.forEach(review => {
          ratingCounts[review.rating]++;
        });
        
        const ratingDistribution: {[key: number]: number} = {};
        for (const rating in ratingCounts) {
          ratingDistribution[rating] = (ratingCounts[rating] / totalReviews) * 100;
        }
        
        setMetrics({
          averageRating,
          totalReviews,
          ratingDistribution
        });
        
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch reviews");
        setLoading(false);
      }
    }, 800);
  }, []);

  return {
    reviews,
    metrics,
    loading,
    error
  };
};
